import React, { useEffect, useState } from 'react';
import { Check, MapPin, Pencil, X } from 'lucide-react';
import type {
  AddressEditorProps,
  AddressInputRenderProps,
  SurveyAddressDraft,
} from './types';

const DEFAULT_LABEL = 'Site location';
const DEFAULT_PLACEHOLDER = 'Street address, city, state...';

/** Merge a display string + any parsed structured fields onto a base draft. */
function applyInput(
  base: SurveyAddressDraft | undefined,
  formatted: string,
  parsed?: Partial<SurveyAddressDraft>,
): SurveyAddressDraft {
  return { ...(base ?? {}), ...(parsed ?? {}), formatted };
}

function DefaultInput({ value, onChange, placeholder, autoFocus }: AddressInputRenderProps): React.ReactElement {
  return (
    <input
      type="text"
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label="Address"
      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#007ACC]"
    />
  );
}

/**
 * Props-driven survey address editor shared by both field PWAs (audit M13).
 *
 * - Inline mode (`onChange`, no `onSave`): always-editing text field; every edit
 *   reports the merged draft. The Sales app injects a Places autocomplete input.
 * - Toggle mode (`onSave`): view (formatted + pencil) → edit (input + optional
 *   draggable map + Save/Cancel). The main client injects its map and persists
 *   on Save.
 *
 * The Google Maps integration lives in each app and is injected via
 * `renderInput` / `renderMap`, so this package never depends on a Maps SDK.
 */
export function AddressEditor({
  value,
  onChange,
  onSave,
  renderInput,
  renderMap,
  label = DEFAULT_LABEL,
  hideLabel = false,
  placeholder = DEFAULT_PLACEHOLDER,
}: AddressEditorProps): React.ReactElement {
  const toggleMode = Boolean(onSave);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SurveyAddressDraft>(value ?? {});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Keep the draft in sync with incoming props while not actively editing.
  // Inline mode renders straight off `value` and never reads `draft`, so skip
  // it there — inline callers pass a fresh `value` literal every render, which
  // would otherwise fire this effect (and an extra re-render) on every unrelated
  // parent update.
  useEffect(() => {
    if (toggleMode && !editing) setDraft(value ?? {});
  }, [value, editing, toggleMode]);

  const renderInputSurface = (props: AddressInputRenderProps): React.ReactNode =>
    renderInput ? renderInput(props) : <DefaultInput {...props} />;

  // -- Inline mode: no view/edit toggle, edits reported live. ----------------
  if (!toggleMode) {
    const handleInline = (formatted: string, parsed?: Partial<SurveyAddressDraft>) => {
      onChange?.(applyInput(value, formatted, parsed));
    };
    const handlePin = (lat: number, lng: number) => {
      onChange?.({ ...(value ?? {}), lat, lng });
    };
    return (
      <div className="flex flex-col gap-2">
        {!hideLabel && (
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</p>
        )}
        {renderInputSurface({ value: value?.formatted ?? '', onChange: handleInline, placeholder })}
        {renderMap?.({ lat: value?.lat, lng: value?.lng, onPinChange: handlePin })}
      </div>
    );
  }

  // -- Toggle mode: view / edit with Save + Cancel. --------------------------
  const startEdit = () => {
    setDraft(value ?? {});
    setSaveError(null);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(value ?? {});
    setSaveError(null);
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const next: SurveyAddressDraft = {
        ...draft,
        // A blank field must not wipe a previously-saved formatted address.
        formatted: draft.formatted?.trim() || value?.formatted,
      };
      await onSave?.(next);
      setEditing(false);
    } catch {
      // A rejecting onSave (a caller that doesn't swallow its own errors) must
      // not become an unhandled rejection that silently drops the edit. Keep
      // the editor open with an inline message so the rep can retry.
      setSaveError('Could not save the address. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#E6F2FB] text-[#007ACC]">
          <MapPin className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</p>
          {value?.formatted ? (
            <p className="text-sm font-medium text-slate-900">{value.formatted}</p>
          ) : (
            <p className="text-sm italic text-slate-400">No address captured</p>
          )}
        </div>
        <button
          type="button"
          onClick={startEdit}
          aria-label="Edit address"
          className="flex min-h-[44px] min-w-[44px] flex-none items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[#007ACC]/20 bg-[#E6F2FB]/30 p-3">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 flex-none text-[#007ACC]" />
        <p className="text-xs font-semibold uppercase tracking-wide text-[#007ACC]">Edit {label.toLowerCase()}</p>
      </div>

      {renderInputSurface({
        value: draft.formatted ?? '',
        autoFocus: true,
        placeholder,
        onChange: (formatted, parsed) => setDraft((d) => applyInput(d, formatted, parsed)),
      })}

      {renderMap?.({
        lat: draft.lat,
        lng: draft.lng,
        onPinChange: (lat, lng) => setDraft((d) => ({ ...d, lat, lng })),
      })}

      {saveError && <p className="text-xs text-red-600">{saveError}</p>}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="flex min-h-[44px] items-center gap-1.5 rounded-xl bg-[#007ACC] px-4 text-sm font-medium text-white disabled:opacity-60"
        >
          <Check className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="flex min-h-[44px] items-center gap-1.5 rounded-xl border border-slate-300 px-4 text-sm text-slate-600"
        >
          <X className="h-4 w-4" />
          Cancel
        </button>
      </div>
    </div>
  );
}
